/**
 * Mitgliederverwaltung. "Rang" ist ein reines Datenfeld je Mitglied
 * (frei definierbar, z.B. Prospect/Officer/President) - unabhängig von den
 * System-Rollen, die den Zugriff auf dieses Modul selbst steuern.
 *
 * Standardsortierung ist die Rangordnung (President zuerst, Hangaround
 * zuletzt), nicht alphabetisch - siehe RANK_WEIGHT_SQL in
 * backend/src/routes/members.ts. Die Anzeige "Aktuelle Mitglieder" zählt
 * IMMER alle Mitglieder (unabhängig von Suche/Rang-Filter der Liste, siehe
 * GET /api/members/count) und wird nach jeder Änderung (Anlegen/Bearbeiten/
 * Löschen) automatisch neu geladen.
 */
import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../api/client";
import { usePermission } from "../../hooks/usePermission";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { StatCard } from "../../components/ui/StatCard";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import { MemberForm } from "./MemberForm";
import { formatDate, formatDateTime } from "../../utils/formatDate";
import type { Member, PaginatedResult } from "../../types";

export default function Members() {
  const canCreate = usePermission("members.create");
  const canEditAny = usePermission("members.edit") || usePermission("members.edit_phone") || usePermission("members.change_rank") || usePermission("members.edit_notes");
  const canDelete = usePermission("members.delete");

  const [members, setMembers] = useState<Member[]>([]);
  const [total, setTotal] = useState(0);
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [ranks, setRanks] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [rankFilter, setRankFilter] = useState("");
  const [sort, setSort] = useState<"name" | "rank">("rank");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Member | null | undefined>(undefined);

  const pageSize = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize), sort, dir });
      if (search) params.set("search", search);
      if (rankFilter) params.set("rank", rankFilter);
      const result = await api.get<PaginatedResult<Member>>(`/api/members?${params}`);
      setMembers(result.items);
      setTotal(result.total);
    } finally {
      setLoading(false);
    }
  }, [page, search, rankFilter, sort, dir]);

  const loadRanks = useCallback(() => {
    api.get<string[]>("/api/members/ranks").then(setRanks).catch(() => {});
  }, []);

  const loadCount = useCallback(() => {
    api.get<{ count: number }>("/api/members/count").then((r) => setMemberCount(r.count)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRanks(); }, [loadRanks]);
  useEffect(() => { loadCount(); }, [loadCount]);

  function toggleSort(col: "name" | "rank") {
    if (sort === col) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(col); setDir("asc"); }
  }

  async function handleDelete(m: Member) {
    if (!confirm(`Mitglied "${m.first_name} ${m.last_name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/api/members/${m.id}`);
      load();
      loadCount();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const sortArrow = (col: "name" | "rank") => (sort === col ? (dir === "asc" ? " ↑" : " ↓") : "");

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-100">Mitglieder</h1>
          <p className="text-sm text-gray-500 mt-1">Club-Mitgliederliste — unabhängig von den System-Benutzerkonten.</p>
        </div>
        {canCreate && <button className="btn-primary" onClick={() => setEditing(null)}>+ Neues Mitglied</button>}
      </div>

      <div className="max-w-xs">
        <StatCard
          label="Aktuelle Mitglieder"
          value={memberCount === null ? "…" : String(memberCount)}
          icon={<span>🪪</span>}
        />
      </div>

      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            className="input sm:max-w-xs"
            placeholder="Suche nach Name oder Telefon…"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
          />
          <select className="input sm:max-w-[200px]" value={rankFilter} onChange={(e) => { setPage(1); setRankFilter(e.target.value); }}>
            <option value="">Alle Ränge</option>
            {ranks.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </Card>

      <Card padded={false} className="overflow-x-auto">
        {loading ? (
          <Spinner />
        ) : members.length === 0 ? (
          <EmptyState message="Keine Mitglieder gefunden." />
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th className="cursor-pointer select-none hover:text-sand" onClick={() => toggleSort("name")}>Name{sortArrow("name")}</th>
                <th>Telefon</th>
                <th className="cursor-pointer select-none hover:text-sand" onClick={() => toggleSort("rank")}>Rang{sortArrow("rank")}</th>
                <th>Notiz</th>
                <th>Erstellt</th>
                <th>Letzte Änderung</th>
                {(canEditAny || canDelete) && <th />}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id}>
                  <td className="font-medium text-gray-100">
                    {m.first_name} {m.last_name}
                    {!!m.exclude_from_count && (
                      <span
                        className="ml-1.5 text-xs text-gray-500 font-normal"
                        title={`Zählt nicht als aktives Mitglied bei "Aktuelle Mitglieder".`}
                      >
                        Ø
                      </span>
                    )}
                  </td>
                  <td className="text-gray-400">{m.phone ?? "—"}</td>
                  <td>
                    {m.rank ? <Badge tone="accent">{m.rank}</Badge> : <span className="text-gray-500">—</span>}
                    {m.linked_username && (
                      <span
                        className="ml-1.5 text-xs text-gray-500"
                        title={`Verknüpft mit Benutzerkonto "${m.linked_username}".`}
                      >
                        🔗
                      </span>
                    )}
                  </td>
                  <td className="text-gray-500 max-w-xs truncate" title={m.note ?? ""}>{m.note ?? "—"}</td>
                  <td className="text-gray-500 text-xs whitespace-nowrap">{formatDate(m.created_at)}</td>
                  <td className="text-gray-500 text-xs whitespace-nowrap">
                    {formatDateTime(m.updated_at)}
                    {m.updated_by_name && <span className="block text-gray-600">von {m.updated_by_name}</span>}
                  </td>
                  {(canEditAny || canDelete) && (
                    <td className="text-right whitespace-nowrap">
                      {canEditAny && <button className="text-accent text-xs hover:underline mr-3" onClick={() => setEditing(m)}>Bearbeiten</button>}
                      {canDelete && <button className="text-danger text-xs hover:underline" onClick={() => handleDelete(m)}>Löschen</button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <button className="btn-secondary px-3 py-1" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Zurück</button>
          <span className="text-gray-400">Seite {page} von {totalPages}</span>
          <button className="btn-secondary px-3 py-1" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Weiter</button>
        </div>
      )}

      {editing !== undefined && (
        <MemberForm
          member={editing}
          ranks={ranks}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); load(); loadRanks(); loadCount(); }}
        />
      )}
    </div>
  );
}
